import React, { useState, useEffect } from 'react';
import { Button, Form, Upload, Modal, App, Input } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import styles from './ProductDetailTemplate7.module.scss';
import type { UploadFile, UploadProps } from 'antd';
import { useAppSelector } from '@/store/hooks';
import icon1 from '@/assets/images/templete7.jpg';
const { TextArea } = Input;

interface ImageUrl {
  url: string;
  name: string;
}

export interface ProductDetailTemplate7Props {
  data: {
    name: string;
    title: string;
    content: string;
    imgUrl: ImageUrl[];
  };
  onChange?: (data: any, selfIndex: number) => void;
  editable?: boolean;
  selfIndex?: number;
  thumbnailMode?: boolean;//是否是缩略图模式
  onDelete?: (selfIndex: number) => void;
}

const ProductDetailTemplate7: React.FC<ProductDetailTemplate7Props> = ({
  data,
  onChange,
  editable = true,
  selfIndex = 0,
  thumbnailMode = false,
  onDelete
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);

  // 编辑表单数据
  const [title, setTitle] = useState(data?.title || '');
  const [content, setContent] = useState(data?.content || '');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 获取token
  const token = useAppSelector((state) => state.user.token);

  // 初始化数据
  useEffect(() => {
    if (data) {
      setTitle(data.title || '');
      setContent(data.content || '');

      // 设置图片列表
      if (data.imgUrl && data.imgUrl.length > 0) {
        const images = data.imgUrl.map((item, index) => ({
          uid: `-${index}`,
          name: item.name || `image-${index}`,
          status: 'done' as const,
          url: item.url
        }));
        setFileList(images);
      }
    }
  }, [data]);

  // 图片上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".mp4,.webm,.ogg,.mov,.avi",
    headers: {
      token: `${token}`
    },
    listType: 'picture-card',
    fileList: fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Image must be smaller than 10MB!");
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      // 复制一份文件列表
      const newFileList = [...info.fileList];

      // 对文件状态进行处理
      newFileList.forEach(file => {
        if (file.response) {
          file.url = file?.response?.data?.url;
        }
      });

      // 更新状态
      setFileList(newFileList);

      if (info.file.status === 'done') {
        message.success(`${info.file.name} uploaded successfully`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} upload failed`);
      }
    },

  };

  // 打开编辑模式
  const handleEdit = () => {
    setIsEditing(true);
    form.setFieldsValue({
      title,
      content
    });
  };

  // 保存编辑
  const handleSave = () => {
    form.validateFields().then(values => {
      const updatedData = {
        ...data,
        title: values.title,
        content: values.content,
        imgUrl: fileList.map(file => ({
          url: file.url || '',
          name: file.name || ''
        }))
      };

      onChange && onChange(updatedData, selfIndex);
      setTitle(values.title);
      setContent(values.content);
      setIsEditing(false);
      message.success('Template updated successfully');
    });
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    // 恢复原始数据
    if (data) {
      setFileList(
        data.imgUrl.map((item, index) => ({
          uid: `-${index}`,
          name: item.name || `image-${index}`,
          status: 'done' as const,
          url: item.url
        }))
      );
    }
  };

  // 上传按钮
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div className={styles['template-container']}>
      {/* 展示模式 */}


      {thumbnailMode ? (
        <div className={styles['template-thumbnail']} onClick={handleEdit}>
          <div className={styles['thumbnail-number']}>
            {selfIndex + 1}
          </div>
          <img className={styles['thumbnail-image']} src={icon1 || ''} alt="" />
          <div className={styles['thumbnail-actions']}>
            <Button 
              size="small"
              danger
              className={styles['delete-btn']}
              onClick={(e) => {
                e.stopPropagation();
                // 这里需要调用删除函数，但需要从父组件传入
                onDelete && onDelete(selfIndex);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles['template-content']} >
          <div className={styles['text-container']}>
            {title && <div className={styles['template-title']}>{title}</div> }
            {content && <div className={styles['template-description']}>{content}</div>}
          </div>
          {/* video封面 */}
          {/* poster="https://www.easysolarsupply.com/api/upload_files/090c1c01851c44b69769e3294beba6ba.png" */}

          <video 
          className={styles['template-image']} 
          src={fileList[0]?.url || ''} 
          controls 
          controlsList="nodownload noremoteplayback"
          muted
          />
        </div>
      )}

      

      {/* 编辑模式 */}
      {isEditing && (
        <Modal
          title="Edit Template"
          open={isEditing}
          onOk={handleSave}
          onCancel={handleCancel}
          okText="Save"
          cancelText="Cancel"
          className={styles['edit-modal']}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              title,
              content
            }}
          >
            <Form.Item
              label="Title"
              name="title"
            >
              <Input placeholder="Enter title" maxLength={50} />
            </Form.Item>

            <Form.Item
              label="Content"
              name="content"
            >
              <TextArea
                placeholder="Enter content"
                rows={4}
                maxLength={200}
              />
            </Form.Item>

            <Form.Item
              label="Video"
            >
              <Upload {...uploadProps}>
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>
              <div className={styles['upload-hint']}>
                Please upload a high-quality video (Recommended size: 1920x1080px)
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default ProductDetailTemplate7; 